import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_HREFS, scrollToSection, waLink, SITE } from "../data/site";
import { useLang } from "../i18n/LanguageContext";
import { Logo, WhatsAppIcon, EASE } from "./Reveal";
import LangSwitcher from "./LangSwitcher";

export default function Header({ introDone }) {
  const { T } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    setOpen(false);
    document.body.style.overflow = "";
    scrollToSection(e, href);
  };

  const wa = waLink(T.wa.default);

  const toggleMenu = () => {
    setOpen(!open);
    document.body.style.overflow = open ? "" : "hidden";
  };

  return (
    <>
      <motion.header
        data-testid="main-header"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ease-soft ${
          scrolled
            ? "bg-federasol-cream/85 shadow-[0_1px_0_rgba(166,93,71,0.12),0_14px_40px_-20px_rgba(64,49,47,0.25)] backdrop-blur-md"
            : "bg-federasol-cream/60 backdrop-blur-sm"
        } ${introDone ? "" : "-translate-y-full"}`}
        initial={{ y: -80, opacity: 0 }}
        animate={introDone ? { y: 0, opacity: 1 } : {}}
        transition={{ duration: 0.9, delay: 0.35, ease: EASE }}
      >
        <div className="container-x flex h-16 items-center justify-between gap-4 md:h-20">
          <Logo onClick={(e) => go(e, "#inicio")} />

          <nav className="hidden items-center gap-6 xl:flex" aria-label="Navegación principal">
            {NAV_HREFS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => go(e, l.href)}
                data-testid={`nav-${l.key}`}
                className="nav-link whitespace-nowrap text-[13px] font-medium text-federasol-cocoa/80 transition-colors hover:text-federasol-cocoa"
              >
                {T.nav[l.key]}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden lg:block">
              <LangSwitcher />
            </div>
            <a
              href={wa}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-whatsapp-button"
              className="btn-primary hidden !px-6 !py-3 sm:inline-flex"
            >
              <WhatsAppIcon className="h-4 w-4" />
              {T.nav.reservar}
            </a>
            <button
              data-testid="menu-toggle-button"
              onClick={toggleMenu}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-federasol-terra/25 text-federasol-cocoa transition-colors hover:bg-federasol-blush xl:hidden"
              aria-label={open ? T.nav.menuCerrar : T.nav.menuAbrir}
              aria-expanded={open}
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-40 flex flex-col bg-federasol-cream/98 px-8 pb-10 pt-28 backdrop-blur-lg xl:hidden"
            initial={{ opacity: 0, y: -18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -18 }}
            transition={{ duration: 0.45, ease: EASE }}
          >
            <div className="mb-6">
              <LangSwitcher mobile />
            </div>
            <nav className="flex flex-1 flex-col gap-1" aria-label="Navegación móvil">
              {NAV_HREFS.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => go(e, l.href)}
                  data-testid={`mobile-nav-${l.key}`}
                  className="border-b border-federasol-terra/10 py-4 font-serif text-3xl font-medium text-federasol-cocoa transition-colors hover:text-federasol-terra"
                  initial={{ opacity: 0, x: -18 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.05, duration: 0.5, ease: EASE }}
                >
                  {T.nav[l.key]}
                </motion.a>
              ))}
            </nav>
            <a
              href={wa}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-whatsapp-button"
              className="btn-primary mt-8 w-full"
            >
              <WhatsAppIcon className="h-5 w-5" />
              {T.nav.reservar} · {SITE.whatsappDisplay}
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
